import { axiosInstance } from './api';

export interface DailyReportPhoto {
  image_url: string;
  caption: string;
  /** ISO timestamp of the underlying frame; null when the source row had no frame. */
  at?: string | null;
  /** Layout-only: rack the photo belongs to. */
  rack_name?: string;
  /** Layout-only: rack category (Готовая еда / Выпечка / Десерты). New reports
   *  carry it; older stored reports fall back to rack_name. */
  rack_type?: string;
  /** Layout-only: rack fill percentage at the captured frame. */
  fill_pct?: number | null;
  /** Queue-only: people in the queue zone at the captured frame. */
  captured_occupancy?: number | null;
  /** Tables-only: the dirty table this photo belongs to. */
  table_name?: string;
  /** Tables-only: 0-based dirty-episode index (a table can be dirty twice a
   *  day, so grouping by name would merge distinct incidents). Old stored
   *  reports lack it — fall back to grouping by table_name. */
  episode?: number;
  /** Tables-only: the episode's REAL span in minutes, alert → first clean
   *  frame. Old reports lack it — fall back to deriving from frame times. */
  cleared_after_min?: number;
  /** Tables-only: ISO of the first clean frame after the episode. */
  cleared_at?: string | null;
  /** Tables-only: the dirty table's PK. Half of the exclusion key (#264) —
   *  pass back unchanged to excludeTableIncident. Old reports lack it. */
  table_id?: number;
  /** Tables-only: ISO of the dirty stretch's START (not `at`, which is one
   *  sampled frame within it). The other half of the exclusion key (#264) —
   *  stable across an exclude+rebuild, unlike `episode` whose index shifts
   *  when a slot is backfilled. Old reports lack it. */
  stretch_start?: string;
}

export interface DailyReportPhotoHighlights {
  layout?: DailyReportPhoto[];
  queue?: DailyReportPhoto[];
  /** All confirmed-dirty table episodes of the day, chronological. */
  tables?: DailyReportPhoto[];
  /** True when the zone monitors tables at all — distinguishes "no dirty
   *  tables today" (show a positive note) from "not configured" (hide).
   *  Absent on reports generated before 2026-07-04. */
  tables_configured?: boolean;
  /** Автоотправка отложила отчёт на проверку (#3005): почему он здесь, а не ушёл сам. */
  auto_send_hold?: { at: string; reasons: string[] };
}

export interface DailyReportDto {
  id: number;
  zone_id: number;
  period_day: string;
  body_html: string;
  dashboards_json: string[];
  generated_at: string;
  status: 'pending' | 'confirmed' | 'cancelled';
  /** When the prod-chat push actually happened; null = confirmed but never
   *  delivered (the confirm≠send gap). */
  sent_at?: string | null;
  /** /send/ only: the report was confirmed but the prod-chat push failed —
   *  the BE deliberately returns 200 with these set instead of a 500, so the
   *  validator can fix the chat rather than see the row stranded (#23). */
  delivery_failed?: boolean;
  delivery_error?: string;
  /** True when the system auto-cancelled this report at generation time
   *  because data coverage was below the per-section threshold. The Send
   *  button is hidden for these rows and a Confirm action will keep them
   *  cancelled (no push to prod). */
  auto_no_data: boolean;
  /** Photo strips with captions. Stored separately because Telegram doesn't
   *  allow <img> in HTML messages — TG copy is text-only, web renders both. */
  photo_highlights?: DailyReportPhotoHighlights | null;
  /** Relative API path for the composited "Самая длинная очередь" preview
   *  (polygon outline + corner pill/robot badge), rendered on demand — see
   *  daily_report_queue_photo_preview. null when this report has no queue
   *  highlight. Present regardless of the send-side feature gate — this is
   *  the review surface for it (Alexey 2026-09-02). */
  queue_photo_preview_url?: string | null;
}

export interface DailyReportPage {
  count: number;
  num_pages: number;
  page: number;
  results: DailyReportDto[];
}

export async function getDailyReports(
  zoneId: number | string,
  from?: string,
  to?: string,
  page = 1,
): Promise<DailyReportPage> {
  const params: Record<string, string | number> = { zone_id: zoneId, page };
  if (from) params.from = from;
  if (to) params.to = to;
  const res = await axiosInstance.get<DailyReportPage>('/core/api/daily-reports/', { params });
  return res.data;
}

export async function getDailyReport(id: number): Promise<DailyReportDto> {
  const res = await axiosInstance.get<DailyReportDto>(`/core/api/daily-reports/${id}/`);
  return res.data;
}

export async function patchDailyReport(id: number, body_html: string): Promise<DailyReportDto> {
  const res = await axiosInstance.patch<DailyReportDto>(
    `/core/api/daily-reports/${id}/`,
    { body_html },
  );
  return res.data;
}

export async function confirmDailyReport(id: number): Promise<DailyReportDto> {
  const res = await axiosInstance.post<DailyReportDto>(`/core/api/daily-reports/${id}/confirm/`);
  return res.data;
}

export async function cancelDailyReport(id: number): Promise<DailyReportDto> {
  const res = await axiosInstance.post<DailyReportDto>(`/core/api/daily-reports/${id}/cancel/`);
  return res.data;
}

/** Composited "Самая длинная очередь" preview (polygon outline + corner
 *  pill/robot badge) — same blob-fetch shape as getVisitSendPreview, since
 *  the daily-reports API requires the JWT Authorization header a plain
 *  <img src> wouldn't send. Call only when
 *  DailyReportDto.queue_photo_preview_url is set. */
export const getQueuePhotoPreview = (reportId: number) =>
  axiosInstance.get<Blob>(`/core/api/daily-reports/${reportId}/queue-photo-preview/`, {
    responseType: 'blob',
  });

export async function sendDailyReport(id: number): Promise<DailyReportDto> {
  const res = await axiosInstance.post<DailyReportDto>(`/core/api/daily-reports/${id}/send/`);
  return res.data;
}

/** Exclude one dirty-table incident from the report's tables card, with the
 *  freed slot auto-backfilled from the next-longest qualifying stretch —
 *  daily_report.manage only (#264). `tableId`/`stretchStart` come straight
 *  off the photo entry (`table_id`/`stretch_start`) — they form the stable
 *  identity of the incident (an `episode` index shifts once a slot is
 *  backfilled, so it can't be used to target a later exclude). */
export async function excludeTableIncident(
  id: number,
  tableId: number,
  stretchStart: string,
): Promise<DailyReportDto> {
  const res = await axiosInstance.post<DailyReportDto>(
    `/core/api/daily-reports/${id}/exclude-table-incident/`,
    { table_id: tableId, stretch_start: stretchStart },
  );
  return res.data;
}

/** Report correction follows the annotation cascade; skipped heads are omitted. */
export type TableIncidentCorrection = {
  occupied: boolean;
  table_in_frame?: boolean;
  personal_items_table?: boolean;
  native_objects_table?: boolean;
  food_dishes_table?: boolean;
  dirty_dishes_table?: boolean;
};

/** Which part of the episode a correction covers: the whole episode (default),
 *  from one photo to the episode end («старт грязь, финиш нативные»), or that
 *  one photo only. Partial cuts keep the dirty prefix as a shorter episode. */
export type TableCorrectionScope = 'episode' | 'from_here' | 'only_this';

/** Updates report/statistics and exports up to five crops with human labels.
 *  Confirmed food/dirty dishes keep the episode; a false whole episode is hidden.
 *  With `cut`, only the rows from/at the photo's `at` are corrected. */
export async function correctTableIncident(
  id: number,
  tableId: number,
  stretchStart: string,
  state: TableIncidentCorrection,
  cut?: { scope: Exclude<TableCorrectionScope, 'episode'>; fromAt: string },
): Promise<DailyReportDto> {
  const res = await axiosInstance.post<DailyReportDto>(
    `/core/api/daily-reports/${id}/correct-table-incident/`,
    {
      table_id: tableId, stretch_start: stretchStart, labels: state,
      ...(cut ? { scope: cut.scope, from_at: cut.fromAt } : {}),
    },
  );
  return res.data;
}

export interface DailyReportQueueEntry {
  id: number;
  zone_id: number;
  zone_name: string;
}

export type DailyReportQueueKind = 'pending' | 'unsent';

export interface DailyReportQueue {
  date: string;
  kind: DailyReportQueueKind;
  results: DailyReportQueueEntry[];
}

/** Returns the list of reports needing validator action for a date,
 *  ordered by zone name. Manager-only.
 *
 *    kind='pending' (default) — status=pending; awaiting validation
 *    kind='unsent'            — status=confirmed and not yet pushed to prod chat
 */
export async function getDailyReportQueue(
  date: string,
  kind: DailyReportQueueKind = 'pending',
): Promise<DailyReportQueue> {
  const res = await axiosInstance.get<DailyReportQueue>('/core/api/daily-reports/queue/', {
    params: { date, kind },
  });
  return res.data;
}
